import React from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import {
  useFinOpsSummary,
  useFinOpsByEnvironment,
  useFinOpsRecommendations,
  useBillingForecast,
} from "src/domains/application/hooks/useBilling";
import { EmptyState } from "src/shell/components/EmptyState";
import { ErrorMessage } from "src/shell/components/ErrorMessage";
import { getCardStyles, sectionLabelSx } from "src/shell/styles/themeUtils";

import type {
  CostByTeam,
  CostByEnvironment,
  FinOpsRecommendation,
} from "../../types/OrganizationTypes";

interface FinOpsTabProps {
  orgAlias: string;
}

const FinOpsTab: React.FC<FinOpsTabProps> = () => {
  const theme = useTheme();
  const {
    data: summary,
    loading: summaryLoading,
    error: summaryError,
  } = useFinOpsSummary();
  const {
    data: byEnvironment,
    loading: envLoading,
    error: envError,
  } = useFinOpsByEnvironment();
  const {
    data: recommendations,
    loading: recsLoading,
    error: recsError,
  } = useFinOpsRecommendations();
  const {
    data: forecast,
    loading: forecastLoading,
    error: forecastError,
  } = useBillingForecast();

  const cardSx = getCardStyles(theme);

  if (summaryLoading) {
    return (
      <Stack spacing={3}>
        <SkeletonBox />
        <SkeletonBox />
      </Stack>
    );
  }

  if (summaryError) {
    return <ErrorMessage message={summaryError} sx={{ my: 2 }} />;
  }

  if (!summary) return null;

  const complianceColor =
    summary.tagCompliancePct >= 80
      ? theme.palette.success.main
      : summary.tagCompliancePct >= 50
        ? theme.palette.warning.main
        : theme.palette.error.main;

  return (
    <Grid container spacing={2}>
      {/* Tag Compliance */}
      <Grid item xs={12} md={6}>
        <Card sx={cardSx}>
          <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
            Tag Compliance
          </Typography>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ color: complianceColor }}
            >
              {summary.tagCompliancePct}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {summary.taggedRecords} of {summary.totalRecords} records tagged
            </Typography>
          </Stack>
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={summary.tagCompliancePct}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.divider, 0.2),
                "& .MuiLinearProgress-bar": {
                  bgcolor: complianceColor,
                  borderRadius: 3,
                },
              }}
            />
          </Box>
          <Stack spacing={1.5}>
            <ComplianceRow
              label="Required tags"
              total={summary.requiredTags.total}
              compliant={summary.requiredTags.compliant}
              color="warning"
            />
            <ComplianceRow
              label="Optional tags"
              total={summary.optionalTags.total}
              compliant={summary.optionalTags.compliant}
              color="success"
            />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Untagged resources
              </Typography>
              <Typography variant="body2" fontWeight={600} color="error.main">
                {summary.untaggedResourceCount}
              </Typography>
            </Stack>
          </Stack>
        </Card>
      </Grid>

      {/* Cost breakdowns */}
      <Grid item xs={12} md={6}>
        <Stack spacing={2}>
          <CostListCard
            title="Cost by Team"
            rows={summary.byTeam.slice(0, 3).map((item: CostByTeam) => ({
              label: item.teamName,
              value: `$${item.mtdCostUsd}`,
            }))}
          />
          <CostListCard
            title="Cost by Environment"
            rows={
              envError
                ? []
                : envLoading
                  ? []
                  : (byEnvironment ?? [])
                      .slice(0, 3)
                      .map((item: CostByEnvironment) => ({
                        label: item.environment,
                        value: `$${item.mtdCostUsd}`,
                      }))
            }
            loading={envLoading}
          />
        </Stack>
      </Grid>

      {/* Recommendations */}
      <Grid item xs={12}>
        <Card sx={cardSx}>
          <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
            Recommendations
          </Typography>
          {recsError ? (
            <ErrorMessage message={recsError} />
          ) : recsLoading ? (
            <CircularProgress size={16} />
          ) : !recommendations?.length ? (
            <EmptyState
              icon={InfoOutlinedIcon}
              title="No recommendations"
              subtitle="Recommendations appear once there is enough usage history."
            />
          ) : (
            <Stack spacing={0}>
              {recommendations.map((rec: FinOpsRecommendation, i: number) => (
                <Stack
                  key={rec.title}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    py: 1.5,
                    px: 1.5,
                    borderBottom:
                      i < recommendations.length - 1
                        ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                        : "none",
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {rec.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {rec.subtitle}
                    </Typography>
                  </Box>
                  <Chip
                    label={`Save $${rec.potentialMonthlySavingsUsd}/mo`}
                    size="small"
                    color="success"
                    sx={{ height: 22, fontSize: "0.75rem" }}
                  />
                </Stack>
              ))}
            </Stack>
          )}
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={cardSx}>
          <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
            Cost Forecast
          </Typography>
          {forecastLoading ? (
            <CircularProgress size={16} />
          ) : forecastError || !forecast ? (
            <EmptyState
              icon={InfoOutlinedIcon}
              title="Forecast unavailable"
              subtitle="A cost forecast will appear when billing data is available."
            />
          ) : (
            <Stack spacing={1}>
              <ForecastRow
                label="Current MTD"
                value={`$${forecast.currentMonthUsd}`}
                emphasized
              />
              <ForecastRow
                label="Projected"
                value={`$${forecast.forecastUsd}`}
              />
              <ForecastRow
                label="Next period estimate"
                value={`$${forecast.nextPeriodEstimateUsd}`}
              />
            </Stack>
          )}
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={cardSx}>
          <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
            Cost Trend (6 months)
          </Typography>
          <EmptyState
            icon={InfoOutlinedIcon}
            title="Historical trend unavailable"
            subtitle="Historical monthly cost data will appear when the time-series source is available."
          />
        </Card>
      </Grid>
    </Grid>
  );
};

function ForecastRow({
  label,
  value,
  emphasized = false,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={emphasized ? 700 : 600}>
        {value}
      </Typography>
    </Stack>
  );
}

function ComplianceRow({
  label,
  total,
  compliant,
  color,
}: {
  label: string;
  total: number;
  compliant: number;
  color: "success" | "warning" | "error";
}) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} color={`${color}.main`}>
        {total === 0 ? "—" : `${compliant}/${total}`}
      </Typography>
    </Stack>
  );
}

function CostListCard({
  title,
  rows,
  loading,
}: {
  title: string;
  rows: { label: string; value: string }[];
  loading?: boolean;
}) {
  const theme = useTheme();
  return (
    <Card sx={getCardStyles(theme)}>
      <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
        {title}
      </Typography>
      {loading ? (
        <CircularProgress size={16} />
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No data yet.
        </Typography>
      ) : (
        <Stack spacing={0}>
          {rows.map((row, i) => (
            <Stack
              key={row.label}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                py: 1,
                px: 1.5,
                borderBottom:
                  i < rows.length - 1
                    ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                    : "none",
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                {row.label}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {row.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Card>
  );
}

function SkeletonBox() {
  const theme = useTheme();
  return (
    <Card sx={getCardStyles(theme)}>
      <Stack spacing={1} sx={{ p: 1 }}>
        <SkeletonItem width={120} />
        <SkeletonItem width="80%" />
        <SkeletonItem width="60%" />
      </Stack>
    </Card>
  );
}

function SkeletonItem({ width }: { width: string | number }) {
  return (
    <Box
      sx={{
        width,
        height: 16,
        borderRadius: 1,
        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
      }}
    />
  );
}

export default FinOpsTab;
